import type { NeaWishlist } from '@expert/types';
import { useCookie, useFetch, useRequestHeaders, useRequestURL, useRoute, useRuntimeConfig, useState } from 'nuxt/app';
import { GridDesignState } from '~~/models/ListOrGrid';
import type { PDSNavigationItem } from '~~/models/Navigation';
import type { UserProfile } from '~~/models/Session';
import type { ActivatedService, ShoppingCart } from '~~/models/shoppingCart';
import defaultFlags from '~~/server/assets/defaultOpenFeatureFlags.json';
import { ProvideNames, StateNames } from '#shared/statics';

export const useEmbedded = () => useState<boolean>('isEmbedded', () => false);

/**
 * Returns the currently selected store
 *
 */
export const useCurrentStore = () => useState<Store>('currentStore');

export const useCurrentStoreLogo = (forceRegular?: boolean) => {
    const pCon = useRuntimeConfig().public;
    const currentStore = useCurrentStore();
    const useTheme = useFeatureState('cfg-seasonal-theme');
    const themeConfig = useFeatureValue('cfg-seasonal-theme');

    const preferredLogo = computed(() => {
      return useTheme ? (themeConfig.value != null && typeof themeConfig.value == "object" && 'logo' in themeConfig.value && themeConfig.value.logo != null && typeof themeConfig.value.logo == 'string' ? themeConfig.value.logo : null) : null;
    });

    return computed(() => {
      const variant = (!forceRegular && (pCon.preferDarkMode || preferredLogo.value == 'white')) ? 'storeLogoDarkImg' : 'storeLogoImg';
      const hasCampaignID = Object.keys(useRoute()?.query ?? {})?.find((para) => para?.toLowerCase() == 'campaignid') != undefined;

      return ((hasCampaignID ? null : currentStore.value))?.[variant] ?? ProvideNames.DEFAULT_CENTRAL_STORE[variant];
    });
};

export const useHomeLayout = () => useState('homeLayout', () => '1');
function getTargetStoreId() {
    const route = useRoute();
    return route.query?.branch_id?.toString() || useCurrentStore()?.value?.id;
}
export const useStartpageConfig = () =>
    useState('useStartpageConfig-' + getTargetStoreId(), () => {
        const route = useRoute();
        const date = computed(() => route.query?.date);
        const store = computed(() => getTargetStoreId());
        const params = {
            store: store.value,
        };
        if(date.value) params.date = date;
        const { data } = useFetch(() => (import.meta.client ? useRuntimeConfig()?.public?.apiUrl : '') + '/api/nea/startpage-service', {
            key: 'startpageConfig-' + store.value + '-' + date.value,
            baseURL: useRuntimeConfig().public.localDevelopment ? 'https://staging.brntgs.expert.de' : undefined,
            params: params,
        });
        return data;
    });

export const useCurrentStoreCompanyGroup = () => {
    return useCurrentStore()?.value?.companyGroupId;
};

export const useCurrentStoreId = () => {
    return useCurrentStore()?.value?.id ?? useStoreCookie()?.value;
};

export const useIsZentrale = computed(() => {
    return useCurrentStoreId() === "e_2879130";
});

interface AppCookieSettings {
    isApp: boolean;
    isNativeApp: boolean;
    hasAskedForPushPermission: boolean;
    hasAskedForLocationPermission: boolean;
    hasAskedForRating: boolean;
}

export const useAppSettingsCookie = () =>
    useCookie<AppCookieSettings>('expertAppConfig', {
        domain: useRuntimeConfig()?.public?.cookieDomain,
        maxAge: 604800,
        default() {
            return {
                isApp: false,
                isNativeApp: false,
                hasAskedForLocationPermission: false,
                hasAskedForPushPermission: false,
                hasAskedForRating: false,
            };
        },
    });
export const useIsGuestCookie = () => useCookie('isGuest', { default: () => true });

export const useContrastMode = (value?: boolean) => {
    const cookie = useCookie('contrastMode', {
        maxAge: 30 * 24 * 60 * 60
    });

    const contrastMode = useState<boolean>('contrastMode', () => cookie.value != null);

    if (value !== undefined) {
        console.log(`contrast-mode: ${value}`);
        contrastMode.value = value;
        if (value) {
            cookie.value = "";
        } else {
            cookie.value = null;
        }
    }
    return contrastMode;
};

export const APP_THEMES = ['default', 'blackweek', 'blue-schimmel-cheese', 'christmas'] as const;
export type AppTheme = typeof APP_THEMES[number];
export function isAppTheme(value: unknown): value is AppTheme {
    return typeof value === 'string' && APP_THEMES.includes(value as AppTheme);
}

export const useSearchWord = () => useState<string>('searchWord');
export const useSuggestionsOpenState = () => useState<boolean>('suggestionsOpen', () => false);
export const useGridDesignState = () => useState<GridDesignState>('gridDesign', () => GridDesignState.LINE);
export const usePDSCartState = () => useState<string>('pdsCartState', () => 'default');
export const usePDSRelatedArticleCheck = () => useState<boolean>('relatedArticleCheck', () => true);

export const usePDSActiveBundleId = () => useState('usePDSActiveBundleId', () => '');
export const usePDSActiveBundlePrice = () => useState<number>('usePDSActiveBundlePrice', () => 0);
export const usePDSActiveBundleReservable = () => useState<boolean>('usePDSActiveBundleReservable', () => true);
export const usePDSNavigationItems = () =>
    useState<Set<PDSNavigationItem>>('pdsNavigationItems', () => new Set<PDSNavigationItem>());

export const useCurrentSessionIdentifier = () =>
    useState('currentIdentifier', useCurrentUserState().value?.sessionIdentifier);

export const useCurrentUserState = () => useState<UserProfile | undefined>('userProfile');

export const useNeoSession = () => useState<NeoSession>(StateNames.NEO_SESSION);

export const useCurrentLoggedInState = (): ComputedRef<boolean> => computed(() => {
    const appUserSession = useState<AppUserSession>(StateNames.APP_USER_SESSION);
    return appUserSession.value.authLevel !== 'ANONYMOUS';
});

/*export const useCurrentLoggedInState = () => {
    const freshUser = useCurrentUserState();
    let loggedIn = false;
    if (freshUser?.value && Array.isArray(freshUser?.value?.roles) && freshUser.value?.roles.length > 1) {
        loggedIn = true;
    }
    if (freshUser && Array.isArray(freshUser.value?.roles) && freshUser.value?.roles.length == 1) {
        loggedIn = freshUser.value?.roles?.[0] == 'ANONYMOUS' ? false : true;
    }
    return { value: loggedIn };
};*/

export const useCurrentShoppingCart = () => useState<ShoppingCart>('currentShoppingCart');

export const useCurrentReservationCart = () => useState<ShoppingCart>('currentReservationCart');

export const useShoppingCartItemsCount = () => useState<number>('shoppingCartItemsCount', () => 0);

export const useReservationCartItemsCount = () => useState<number>('reservationCartItemsCount', () => 0);

export const useActivatedServicesInCart = () => useState<Array<ActivatedService>>('activatedServices', () => []);

export const useCurrentWishList = () => useState<NeaWishlist>('currentWishList');

export const useWishListItemCount = () => useState<number>('wishListItemsCount', () => 0);

export declare interface CookieBotCookie {
    REQUIRED: boolean;
    STATISTICS: boolean;
    MARKETING: boolean;
    PRODUCT_RATINGS: boolean;
}

export enum PermissionMapping {
    REQUIRED = 'necessary',
    STATISTICS = 'statistics',
    MARKETING = 'marketing',
    PRODUCT_RATINGS = 'preferences',
}

export const getCookiePermissions = (permission: PermissionMapping) =>
    useState(
        'cookie'
            + Object.keys(PermissionMapping).find(
                (key) => PermissionMapping[key as keyof typeof PermissionMapping] === permission,
            ),
        () => {
            const cookie = useCookie('CookieConsent');
            if (cookie && cookie.value) {
                const parsedCookie = JSON.parse(cookie.value.replace(/'/g, '"').replace(/([a-zA-Z0-9_]+):/g, '"$1":'));
                return parsedCookie[permission] === true;
            }
            return false;
        },
    );

export declare interface AddressFormData {
    title?: string | undefined;
    company?: string | undefined;
    firstName?: string | undefined;
    lastName?: string | undefined;
    street?: string | undefined;
    houseNumber?: string | undefined;
    zip?: number | undefined;
    city?: string | undefined;
    telephone?: string | undefined;
    email?: string | undefined;
    country?: string | undefined;
    countryCode?: string | undefined;
}

export declare interface CheckoutRegistration {
    invoiceAddress: AddressFormData | undefined;
    login: {
        email: string | undefined;
        password: string | undefined;
    };
}

export const useReservationFormData = () =>
    useState<AddressFormData>('reservationFormData', () => {
        return getDefaultAddress();
    });

export const useReservationFormAdditionalInfo = () =>
    useState<{ comment?: string; newsletterOptIn?: boolean }>('reservationFormAdditionalData', () => {
        return { comment: '', newsletterOptIn: false };
    });

export const useCartFormData = () =>
    useState<{
        insuranceFlag?: boolean;
        newsletterFlag?: boolean;
        termsFlag?: boolean;
        invoiceAddress?: AddressFormData;
        shippingAddress?: AddressFormData;
        registration?: CheckoutRegistration;
    }>('cartFormData', () => {
        return {
            insuranceFlag: false,
            newsletterFlag: false,
            termsFlag: true,
            invoiceAddress: getDefaultAddress(),
            shippingAddress: {
                title: 'UNKNOWN',
                countryCode: 'DE',
                country: 'Deutschland',
            },
            registration: {
                login: {},
            },
        };
    });

export const useCartOverview = () =>
    useState<AddressFormData>('cartOverview', () => {
        return {};
    });

function getDefaultAddress() {
    let defaultAddress = {
        title: 'UNKNOWN',
        countryCode: 'DE',
    };

    const user = useUserProfile().value;
    if (user?.invoiceAddress) {
        defaultAddress = user.invoiceAddress;
    }

    if (defaultAddress.countryCode == 'DE') {
        defaultAddress.country = 'Deutschland';
    }

    return defaultAddress;
}

export const useExpCookieIdentifier = () => useCookie<string | null>('expertShopABIdentifier', { default: () => null });

export const useUserGroup = () => useState('userGroup', () => useABConfigCookie()?.value?.context?.usergroup ?? 'anonymous');

export const mayShowDebugContent = () =>
    computed(
        () =>
            useUserGroup()?.value == 'internal'
            || useFeatureState('bt-dev-debug')?.value
            || useFeatureState('bt-internal-test')?.value,
    );

/**
 *
 * @returns fmarktcookie
 */
export const useStoreCookie = () =>
    useCookie(ProvideNames.STORE_COOKIE_NAME, {
        default() {
            let branchId = useRequestURL()?.searchParams?.get('branch_id');
            let campaignId = useRequestURL()?.searchParams?.get('campaignId') ?? useRequestURL()?.searchParams?.get('campaignid') ?? useRequestURL()?.searchParams?.get('campaignID');

            if (campaignId !== undefined && campaignId !== null) {
                console.warn(`[bt-states] got initial useStoreCookie call with campaign id param ${campaignId}`);
                return ProvideNames.DEFAULT_CENTRAL_STORE.id;
            } else if (branchId !== undefined && branchId !== null) {
                console.warn(`[bt-states] got initial useStoreCookie with branch id param ${branchId}`);
            } else {
                branchId = useRequestHeaders()['fmarktheader'];
                console.log(`[bt-states] no query param using value from header ${branchId}`);
            }

            if (branchId && !branchId.startsWith('e_')) branchId = 'e_' + branchId;

            return branchId;
        },
        domain: useRuntimeConfig()?.public?.cookieDomain,
        path: '/',
        secure: useRuntimeConfig()?.public?.cookieSecure != 'false',
        maxAge: 30 * 3600 * 24,
    });

export const useStoreCookieInitialized = () => useState<boolean>('storeCookieInitialized', () => false);
export const useStoreCookieInitializedCookie = () => useCookie('storeCookieInitialized', { default: () => false });
export const usePaybackDiscountCoinPool = () => useState<number>('usePaybackDiscountCoinPool', () => 0);

export const useCategoryFlyoutState = () => useState<boolean>('categoryFlyoutState', () => false);

export const useSuperMenuState = () => useState<boolean>('useSuperMenuState', () => false);

export const useMobileMenuState = () => useState<boolean>('useMobileMenuState', () => false);

export const useSuperMenuDropdownState = () => useState<string>('useSuperMenuDropdownState', () => '0');
export const useSuperChildMenuDropdownState = () => useState<string>('useSuperMenuDropdownState', () => '0');

export const getScrid = () => useState<string>('scrid-state', () => {
    const val = ref()
    watch(useABConfigCookie(), () => {
        const params = new URLSearchParams((useABConfigCookie()?.value?.context?.campaigns as string ?? ''));
        if (params?.get('scrid') && val.value != params.get('scrid'))
            val.value = params.get('scrid')
    }, { immediate: true })
    return val
});

export interface PopupRequest {
  id: string;
  payload?: unknown;
}

export const usePopupQueue = () => {
  const queue = useState<PopupRequest[]>(
    'popup-queue',
    () => []
  );

  const currentPopup = computed(() => queue.value[0] ?? null);

  const enqueue = (popup: PopupRequest) => {
    queue.value.push(popup);
  };

  const next = () => {
    queue.value.shift();
  };

  const clear = () => {
    queue.value = [];
  };

  return {
    queue,
    currentPopup,
    enqueue,
    next,
    clear,
  };
};



export const stateForStoreFinder = () => useState<boolean>('localStoreFinderPopUp', () => false);

export const stateForStoreFinderFlyOut = () => useState<boolean>('localStoreFinderFlyOut', () => false);

export const gtmGlobalCounter = () => useState<number>('globalGtmCounter', () => 0);

export const gtmDataContainer = () =>
    useState('GtmDataLayerContainer', () => {
        return {
            event: 'reload',
            ecommerce: {
                impressions: [],
                detail: {
                    items: {},
                },
            },
        };
    });

export const useFlagsUsedState = () => useState('flagsUsed', () => new Set<string>());

export const useFeatureState = (feature: string) =>
    useState<boolean>('feature-' + feature?.toLowerCase(), () => {
        useLogger('states').debug('checking for flag ', feature);
        if (useFlagsUsedState().value?.has && !useFlagsUsedState().value?.has(feature?.toLowerCase()))
            useFlagsUsedState().value.add(feature?.toLowerCase());
        return defaultFlags[feature?.toLowerCase()]?.disabled == false;
    });

export const useFlagVariants = () => useState('flagVariants', () => new Map<string, string>());

export const useFeatureValue = (feature: string) =>
    useState('feature-value-' + feature?.toLowerCase(), () => {
        return defaultFlags[feature?.toLowerCase()].variants[defaultFlags[feature?.toLowerCase()].defaultVariant];
    });

export const useIsCrawlerState = () => useState('isCrawler', () => false);

export const useFilteredCategoryTag = () => useState('filteredCategoryTag', () => '');

export const useAppPushCookie = () =>
    useCookie('expertAppPushToken', { maxAge: 30 * 24 * 3600, secure: true, sameSite: 'strict' });

export const useABConfigCookie = () =>
    useCookie<{ context?: any }>('expertShopABConfig', {
        encode(value) {
            if (import.meta.client) return encodeURIComponent(btoa(JSON.stringify(value)));
            else return encodeURIComponent(Buffer.from(JSON.stringify(value), 'ascii').toString('base64'));
        },
        decode(value: string) {
            if (import.meta.client) return JSON.parse(atob(decodeURIComponent(value)));
            else return JSON.parse(Buffer.from(decodeURIComponent(value), 'base64').toString('ascii'));
        },
        maxAge: 72 * 3600,
        secure: useRuntimeConfig()?.public?.cookieSecure != 'false',
        path: '/',
        domain: useRuntimeConfig()?.public?.cookieDomain,
        sameSite: useRuntimeConfig()?.public?.cookieSecure != 'false' ? 'none' : undefined,
    });

export const useGA4State = () =>
    useState<{ gclid: string; client_id: string; session_id: string; session_number: number }>('ga4data', () => {
        return { gclid: '', client_id: '', session_id: '', session_number: 0 };
    });

export const setABContextVariable = (key: string, value: any) => {
    let contextK = useABConfigCookie()?.value ?? { context: {} };
    contextK.context[key] = value;
    useABConfigCookie().value = contextK;
};

export const usePaypalScriptInjected = () => useState('paypalSdkLoaded', () => false);

function createPaypalCacheKey(zero: boolean, pageType: string) {
    return `paypal-${zero ? 'zero' : 'standard'}-${pageType}`;
}

export const usePaypalMessagesInstance = (paypal, pageType: string, zero: boolean = false) =>
    useState(createPaypalCacheKey(zero, pageType), async () => {
        const clientToken = await $fetch('/api/paypal/token', {
            method: 'POST',
            body: {
                zero: zero,
            },
        }).catch((err) => {
            console.warn('Could not get clientToken', err);
            return '-';
        });
        const sdkInstance = await paypal.createInstance({
            clientToken: clientToken,
            pageType: pageType,
        });
        return sdkInstance.createPayPalMessages();
    });

export const useLastNavigationPoint = () => useState('useLastNavigationPoint',()=>"")
